/**
 * Menu screens (doc 11 §Screen map, doc 18 §5) — title, slots, depth select,
 * Colony Command, results. Same pixel buffer and same widget language as the
 * run HUD: nothing here is DOM, nothing here is anti-aliased.
 *
 * The shared frame is deliberately quiet — a pressure-gradient ground, drifting
 * marine snow, one hairline rule under the title — so that the only bright
 * things on a menu are the things you can act on.
 */

import { drawCentered, font } from '../render/font/font.ts';
import { DEPTHS } from '../render/palette.ts';
import { mix, PixelSurface, type Color } from '../render/surface.ts';
import { g, UI } from './theme.ts';
import { contains, drawButton, drawPanel, type Rect, type WidgetState } from './widgets.ts';

export interface MenuItem {
  label: string;
  hotkey?: string;
  /** One quiet line under the row — what this choice actually does. */
  hint?: string;
  disabled?: boolean;
  /** Right-aligned value inside the row: a price, a level, a timestamp. */
  value?: string;
  tone?: 'ink' | 'good' | 'warning' | 'critical';
  /**
   * A caption drawn ABOVE this row, opening a group. Long lists (settings) need
   * it; short ones never set it and pay nothing for the feature.
   */
  section?: string;
}

/**
 * Ambient depth: a vertical water gradient plus a slow drift of marine snow.
 * The snow is a hash of its index, so it never allocates and never drifts out
 * of sync with itself; `t` is seconds of wall-clock (menus may use the clock —
 * only the sim may not).
 */
export function drawBackdrop(s: PixelSurface, t: number): void {
  const top = DEPTHS.d1.waterTop;
  const bottom = DEPTHS.d3.waterBottom;
  for (let y = 0; y < s.height; y++) {
    const k = y / Math.max(1, s.height - 1);
    s.hline(0, s.width - 1, y, mix(bottom, top, (1 - k) * 0.55), 'set');
  }

  // marine snow: 90 specks on deterministic lanes, wrapping down the screen
  const speck = mix(UI.ink, UI.panelFill, 0.55);
  for (let i = 0; i < 90; i++) {
    const lane = (i * 2654435761) % 100000;
    const x = lane % s.width;
    const speed = 6 + (lane % 7);
    const y = Math.floor((((lane / 7) % s.height) + t * speed) % s.height);
    const drift = Math.floor(((t * (2 + (i % 3))) % 6) - 3);
    s.px((x + drift + s.width) % s.width, y, i % 5 === 0 ? UI.accent : speck);
  }

  drawSeafloor(s, t);
}

/**
 * The bottom of every menu is the same seabed the run is fought on: a ridge
 * silhouette, kelp that sways, and a few things down there that glow. It is
 * ambience, so it is cheap and never brighter than the interface.
 */
function drawSeafloor(s: PixelSurface, t: number): void {
  const baseY = Math.round(s.height * 0.82);
  // depth haze: the far ridge washes toward the water, the near one goes black
  const near = mix(DEPTHS.d3.waterBottom, DEPTHS.d1.terrain, 0.22);
  const far = mix(DEPTHS.d1.terrain, DEPTHS.d1.waterBottom, 0.55);
  const nearRidge = (x: number): number =>
    Math.round(baseY + 14 - (Math.sin(x * 0.013 + 2.2) * 6 + Math.sin(x * 0.045) * 2));
  const farRidge = (x: number): number =>
    Math.round(baseY + 4 - (Math.sin(x * 0.021) * 5 + Math.sin(x * 0.007 + 1.7) * 7));

  for (let x = 0; x < s.width; x++) {
    const yf = farRidge(x);
    s.vline(x, yf, s.height - 1, far);
    s.px(x, yf, mix(far, UI.panelRimLight, 0.25));
  }

  // kelp grows from the near ridge and sways; the near ridge covers its roots
  const kelpInk = mix(near, DEPTHS.d1.terrain, 0.6);
  const kelpTip = mix(kelpInk, DEPTHS.d1.playerGlow, 0.22);
  for (let k = 0; k < 18; k++) {
    const seed = (k * 40503) % 9973;
    const x0 = Math.round(((seed % 997) / 997) * (s.width - 8)) + 4;
    const height = 22 + (seed % 50);
    const phase = (seed % 31) / 31;
    const lean = ((seed % 7) - 3) / 3; // each frond grew into its own current
    const root = nearRidge(x0) + 3;
    for (let h = 0; h < height; h++) {
      const y = root - h;
      if (y < 0) break;
      const k2 = h / height;
      const sway = Math.sin(t * 0.6 + phase * 6.28 + h * 0.08) * k2 ** 1.6 * 5 + lean * k2 * k2 * 7;
      const x = Math.round(x0 + sway);
      s.px(x, y, h > height - 4 ? kelpTip : kelpInk);
      if (h < height * 0.35) s.px(x + 1, y, kelpInk); // thicker at the base
      if (h % 11 === 5) s.px(x + (h % 2 === 0 ? 2 : -1), y, kelpInk); // fronds
    }
  }

  for (let x = 0; x < s.width; x++) {
    const yn = nearRidge(x);
    s.vline(x, yn, s.height - 1, near);
    s.px(x, yn, mix(near, UI.panelRimLight, 0.22));
  }

  // vents: slow bioluminescent pulses along the near ridge
  for (let i = 0; i < 6; i++) {
    const seed = (i * 7919) % 4001;
    const x = Math.round((seed / 4001) * (s.width - 20)) + 10;
    const y = nearRidge(x) - 1;
    const pulse = (Math.sin(t * 0.9 + i * 1.7) + 1) / 2;
    if (pulse <= 0.45) continue;
    s.px(x, y, mix(near, DEPTHS.d1.structureEmissive, pulse));
    if (pulse > 0.75) {
      for (const [dx, dy] of [[-1, 0], [1, 0], [0, -1]] as Array<[number, number]>) {
        s.px(x + dx, y + dy, mix(near, DEPTHS.d1.structureEmissive, (pulse - 0.75) * 1.6));
      }
    }
  }
}

export interface FrameOpts {
  title: string;
  subtitle?: string | undefined;
  /** Bottom-left key hints — always say how to get out of a screen. */
  footer?: string | undefined;
  /** Right-aligned stamp: Pearls, version, slot name. */
  stamp?: string | undefined;
}

/** Title block + rules. Returns the content rect below the header. */
export function drawFrame(s: PixelSurface, opts: FrameOpts): Rect {
  const display = font('display');
  const body = font('body');
  const micro = font('micro');
  const x = g(6);

  // a soft lamp behind the title — the one place a menu is allowed to glow
  const titleW = display.measure(opts.title, { caps: true, letterSpacing: 2 });
  glow(s, x + (titleW >> 1), g(5) + (display.capHeight >> 1), Math.max(titleW, 120), display.capHeight * 3);
  display.draw(s, opts.title, x, g(5), UI.ink, { caps: true, letterSpacing: 2, shadow: UI.shadow });
  let y = g(5) + display.capHeight + g(2);
  if (opts.subtitle !== undefined) {
    body.draw(s, opts.subtitle, x, y, UI.inkDim, { caps: true, letterSpacing: 1 });
    y += body.capHeight + g(2);
  }
  s.hline(x, s.width - x - 1, y, UI.panelRimLight);
  if (opts.stamp !== undefined) {
    // measure with the SAME options it is drawn with, or the last letters walk
    // off the right edge on a narrow buffer
    const opt = { caps: true, letterSpacing: 1 } as const;
    body.draw(s, opts.stamp, Math.max(x, s.width - x - body.measure(opts.stamp, opt)), g(5), UI.accent, opt);
  }
  if (opts.footer !== undefined) {
    micro.draw(s, opts.footer, x, s.height - g(4), UI.inkDim, { caps: true, letterSpacing: 1 });
  }
  return { x, y: y + g(3), w: s.width - x * 2, h: s.height - (y + g(3)) - g(6) };
}

/** An elliptical wash of light, quantised to 4 steps so it stays pixel art. */
function glow(s: PixelSurface, cx: number, cy: number, w: number, h: number): void {
  const rx = w >> 1;
  const ry = h >> 1;
  for (let y = -ry; y <= ry; y++) {
    for (let x = -rx; x <= rx; x++) {
      const d = (x * x) / (rx * rx) + (y * y) / (ry * ry);
      if (d >= 1) continue;
      const step = Math.round((1 - d) * 4) / 4;
      if (step <= 0) continue;
      const px = cx + x;
      const py = cy + y;
      s.px(px, py, mix(s.get(px, py), DEPTHS.d1.waterTop, step * 0.35));
    }
  }
}

export const ROW_H = 22;
const ROW_GAP = 6;
/** Width the scrollbar reserves at the right edge of a column. */
export const SCROLLBAR_W = 6;

export interface MenuMetrics {
  /** Content-space y of each row, relative to the column's top. */
  offsets: number[];
  /** One row's full stride, including its hint line. */
  step: number;
  /** Extra height a row's section caption adds above it, by index. */
  lead: number[];
  contentH: number;
}

/** Height a section caption claims above the row it opens. */
export function sectionLead(): number {
  return font('micro').capHeight + g(4);
}

/**
 * Row geometry without drawing it — the screen needs this to keep the selected
 * row on screen BEFORE the frame is drawn, not one frame later.
 */
export function menuMetrics(items: MenuItem[]): MenuMetrics {
  const hasHints = items.some((i) => i.hint !== undefined);
  const step = ROW_H + ROW_GAP + (hasHints ? font('micro').capHeight + 2 : 0);
  const cap = sectionLead();
  const offsets: number[] = [];
  const lead: number[] = [];
  let y = 0;
  for (const it of items) {
    const extra = it.section !== undefined ? cap : 0;
    lead.push(extra);
    y += extra;
    offsets.push(y);
    y += step;
  }
  return { offsets, step, lead, contentH: y };
}

/**
 * Draw a track and thumb for a column that is taller than its view. Small
 * windows are the normal case, not the exception: the UI buffer is only half
 * the window's height at UI scale 2 (doc 17 §1).
 */
export function drawScrollbar(s: PixelSurface, view: Rect, scroll: number, contentH: number): void {
  if (contentH <= view.h) return;
  const x = view.x + view.w - SCROLLBAR_W + 2;
  s.fillRect(x, view.y, 2, view.h, UI.panelRimDark);
  const thumbH = Math.max(12, Math.round((view.h / contentH) * view.h));
  const span = Math.max(1, contentH - view.h);
  const thumbY = view.y + Math.round((scroll / span) * (view.h - thumbH));
  s.fillRect(x, thumbY, 2, thumbH, UI.panelRimLight);
  s.px(x, thumbY, UI.accent);
  s.px(x + 1, thumbY, UI.accent);
}

/** Clamp a scroll offset to what the content actually needs. */
export function clampScroll(scroll: number, view: number, contentH: number): number {
  return Math.max(0, Math.min(scroll, Math.max(0, contentH - view)));
}

/**
 * A column of choices. Rows are fixed height so the eye can track them while
 * the keyboard moves; the hit rects come back for the caller's mouse handling.
 * Rows that would spill out of `r` are not drawn — the scrollbar says why.
 */
export function drawMenu(
  s: PixelSurface,
  r: Rect,
  items: MenuItem[],
  selected: number,
  hover: number,
  scroll = 0,
): Rect[] {
  const body = font('body');
  const micro = font('micro');
  const rects: Rect[] = [];
  const hasHints = items.some((i) => i.hint !== undefined);
  const metrics = menuMetrics(items);
  const scrollable = metrics.contentH > r.h;
  const colW = scrollable ? r.w - SCROLLBAR_W : r.w;
  let y = r.y - scroll;
  for (let i = 0; i < items.length; i++) {
    const it = items[i]!;
    const capY = y;
    y += metrics.lead[i]!;
    const rect: Rect = { x: r.x, y, w: colW, h: ROW_H };
    rects.push(rect);
    // off-view rows keep their (off-view) rect so indices stay aligned
    if (y < r.y || y + metrics.step > r.y + r.h + ROW_GAP) {
      y += metrics.step;
      continue;
    }
    if (it.section !== undefined) {
      micro.draw(s, it.section, rect.x + g(1), capY + g(1), UI.accent, { caps: true, letterSpacing: 2 });
      s.hline(rect.x + g(1), rect.x + colW - g(1), capY + g(1) + micro.capHeight + 2, UI.panelRimDark);
    }
    const state: WidgetState = it.disabled
      ? 'disabled'
      : i === selected
        ? 'selected'
        : i === hover
          ? 'hover'
          : 'default';
    drawButton(s, rect, '', state);
    const ink = it.disabled ? UI.inkDim : toneColor(it.tone);
    body.draw(s, it.label, rect.x + g(2), rect.y + ((ROW_H - body.capHeight) >> 1), ink, { caps: true, letterSpacing: 1 });
    if (it.value !== undefined) {
      const w = body.measure(it.value, { caps: true });
      body.draw(s, it.value, rect.x + rect.w - g(2) - w, rect.y + ((ROW_H - body.capHeight) >> 1), UI.accent, {
        caps: true,
      });
    }
    if (it.hotkey !== undefined) {
      const kw = micro.measure(it.hotkey, { caps: true });
      s.fillRect(rect.x + rect.w - kw - g(2) - 4, rect.y + 3, kw + 3, micro.capHeight + 3, UI.panelRimDark);
      micro.draw(s, it.hotkey, rect.x + rect.w - kw - g(2) - 2, rect.y + 5, UI.inkDim, { caps: true });
    }
    y += ROW_H + ROW_GAP;
    if (it.hint !== undefined) {
      // one line, clipped to the column: a hint that runs off into the backdrop
      // is worse than a hint that stops (doc 10 §Legibility)
      const text = ellipsize('micro', it.hint, colW - g(4), 1);
      micro.draw(s, text, rect.x + g(2), y - ROW_GAP + 1, UI.inkDim, { caps: true, letterSpacing: 1 });
    }
    if (hasHints) y += micro.capHeight + 2;
  }
  drawScrollbar(s, r, scroll, metrics.contentH);
  return rects;
}

/**
 * Trim a string to fit a width, ending in a single dot rather than a cut glyph.
 * `spacing` matches whatever `letterSpacing` the caller draws with, so the
 * measurement and the drawing agree.
 */
export function ellipsize(faceId: 'micro' | 'body', text: string, width: number, spacing = 0): string {
  const f = font(faceId);
  const opts = { caps: true, letterSpacing: spacing };
  if (f.measure(text, opts) <= width) return text;
  let out = text;
  while (out.length > 1 && f.measure(`${out}.`, opts) > width) out = out.slice(0, -1);
  return `${out.trimEnd()}.`;
}

/** Greedy word wrap in a given face — menus carry real sentences. */
export function wrapText(faceId: 'micro' | 'body', text: string, width: number): string[] {
  const f = font(faceId);
  const out: string[] = [];
  let line = '';
  for (const word of text.split(' ')) {
    const candidate = line === '' ? word : `${line} ${word}`;
    if (f.measure(candidate, { caps: true }) <= width || line === '') line = candidate;
    else {
      out.push(line);
      line = word;
    }
  }
  if (line !== '') out.push(line);
  return out;
}

/**
 * Which row is under the pointer. `view` clips the answer, so a row scrolled
 * out of the column can never be clicked through the screen furniture.
 */
export function menuHit(rects: Rect[], x: number, y: number, view?: Rect): number {
  if (view && !contains(view, x, y)) return -1;
  for (let i = 0; i < rects.length; i++) if (contains(rects[i]!, x, y)) return i;
  return -1;
}

export function toneColor(tone: MenuItem['tone']): Color {
  return tone === 'good'
    ? UI.good
    : tone === 'warning'
      ? UI.warning
      : tone === 'critical'
        ? UI.critical
        : UI.ink;
}

/** A modal question, centred: "this erases a colony", "start over?" */
export function drawConfirm(
  s: PixelSurface,
  title: string,
  text: string,
  yesNo: [string, string],
  focus: 0 | 1,
): [Rect, Rect] {
  const body = font('body');
  const header = font('header');
  const w = Math.min(s.width - g(8), 360);
  const h = g(4) + header.capHeight + g(3) + body.capHeight + g(4) + ROW_H + g(3);
  const r: Rect = { x: (s.width - w) >> 1, y: (s.height - h) >> 1, w, h };
  s.fillRect(0, 0, s.width, s.height, mix(UI.shadow, UI.panelFill, 0.35));
  drawPanel(s, r, { raised: true });
  drawCentered(s, header, title, r.x + (r.w >> 1), r.y + g(3), UI.ink, { caps: true, letterSpacing: 1 });
  drawCentered(s, body, text, r.x + (r.w >> 1), r.y + g(3) + header.capHeight + g(3), UI.inkDim, { caps: true });
  const bw = (r.w - g(6)) >> 1;
  const by = r.y + r.h - ROW_H - g(3);
  const yes: Rect = { x: r.x + g(2), y: by, w: bw, h: ROW_H };
  const no: Rect = { x: r.x + r.w - g(2) - bw, y: by, w: bw, h: ROW_H };
  drawButton(s, yes, yesNo[0], focus === 0 ? 'selected' : 'default');
  drawButton(s, no, yesNo[1], focus === 1 ? 'selected' : 'default');
  return [yes, no];
}
